import { createContext, useCallback, useContext } from 'react';

/**
 * Funnel events the modal renderers emit. The library only describes what
 * happened; where it goes (GTM, a product-analytics tool, nowhere) is the
 * host app's call, made through `ModalProvider`'s `onAnalyticsEvent`. That
 * keeps ui-components free of any analytics dependency.
 *
 * Names are deliberately few and generic — GA4 caps distinct event names — so
 * a flow's identity rides in `params.flow`, never in the event name.
 */
export type ModalAnalyticsEventName =
  | 'flow_start'
  | 'step_view'
  | 'step_complete'
  | 'step_back'
  | 'step_error'
  | 'flow_complete'
  | 'flow_cancel'
  | 'flow_abandon'
  | 'modal_open'
  | 'modal_submit'
  | 'modal_dismiss';

export type ModalAnalyticsParamValue = string | number | boolean | undefined;

export type ModalAnalyticsParams = Record<string, ModalAnalyticsParamValue>;

export interface IModalAnalyticsEvent {
  name: ModalAnalyticsEventName;
  params: ModalAnalyticsParams;
}

export type ModalAnalyticsHandler = (event: IModalAnalyticsEvent) => void;

/**
 * Held in its own context rather than on `ModalContext` so a renderer mounted
 * directly (as the multi-step renderer is in its specs) can still find it, and
 * so changing the handler never re-renders every `useModal()` consumer.
 */
export const ModalAnalyticsContext = createContext<
  ModalAnalyticsHandler | undefined
>(undefined);

/**
 * Returns an emitter that never throws: an analytics bug must not be able to
 * break the dialog it is observing.
 */
export function useModalAnalyticsEmitter() {
  const handler = useContext(ModalAnalyticsContext);
  return useCallback(
    (name: ModalAnalyticsEventName, params: ModalAnalyticsParams) => {
      if (!handler) return;
      try {
        handler({ name, params });
      } catch {
        // Swallowed on purpose — see above.
      }
    },
    [handler],
  );
}

/**
 * Stable identifier for a step: an explicit key wins, then a slug of a string
 * title, then the step's position. Titles can be ReactNodes (and get reworded),
 * so callers that care about funnel continuity should pass a key.
 */
export function analyticsStepKey(
  step: { analyticsKey?: string; title?: unknown },
  index: number,
): string {
  if (step.analyticsKey) return step.analyticsKey;
  if (typeof step.title === 'string') {
    const slug = step.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    if (slug) return slug;
  }
  return `step_${index}`;
}

/**
 * How a flow was dismissed without finishing — the `via` of `flow_cancel`.
 * One vocabulary for every flow (the cut sheet wizard uses it too), so "how do
 * people leave" compares across them.
 */
export type FlowCancelVia =
  | 'close_button'
  | 'cancel_button'
  | 'backdrop'
  | 'escape'
  | 'step';

/**
 * Throw from a step's `onNext` (or `onComplete`) to stop on input the user
 * still has to fix. The renderer treats it like any other throw — the wizard
 * stays put and `onError` gets it — but reports `error_type: 'validation'`, so
 * ordinary form validation doesn't read as a failure on error-rate dashboards.
 */
export class MultiStepValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MultiStepValidationError';
  }
}

/**
 * The `error_type` reported for a throw: `validation` for
 * MultiStepValidationError, otherwise the class name only — messages can carry
 * user input.
 * @param error - Whatever was thrown
 * @returns A low-cardinality label for it
 */
export function analyticsErrorType(error: unknown): string {
  if (error instanceof MultiStepValidationError) return 'validation';
  return error instanceof Error ? error.name : typeof error;
}
